import { useMemo, useSyncExternalStore } from "react";

import { can, groupSharedAccess } from "./roles";
import {
  activeOwner,
  getWorkspaceState,
  subscribeWorkspace,
} from "./workspace";

const text = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

// The store's state, for React.
export const useWorkspaceState = () =>
  useSyncExternalStore(
    subscribeWorkspace,
    getWorkspaceState,
    getWorkspaceState
  );

// What screens need from the store's state:
//   enabled      the Teams UI is on (components/teams/flag.js)
//   self         the signed-in username
//   owner        the shared account being worked in, null in your own
//   isShared     working in someone else's account
//   ready        safe to load the account's events (the remembered account
//                has been read and, if any, confirmed by the server)
//   access       your entry for the current account (roles.groupSharedAccess)
//   accounts     every account shared with you, one entry per owner
//   name         what to call the current account ("Acme Funeral Home")
//   profile      the owner's profile as members see it
//   role, scope  your role there ("holder" in your own account) and whether
//                the whole account or only events are shared with you
export function deriveWorkspace(s) {
  const owner = activeOwner(s);
  const accounts = groupSharedAccess(s.shared);
  const access = owner ? accounts.find((a) => a.owner === owner) || null : null;
  const profile = owner ? s.profile : null;
  const profileName = profile
    ? text(profile.company_name) ||
      text([profile.first_name, profile.last_name].filter(Boolean).join(" "))
    : null;
  return {
    enabled: !!s.enabled,
    self: s.self,
    owner,
    isShared: !!owner,
    status: owner ? s.status : "own",
    ready: !s.enabled || !s.self || (s.restored && s.status !== "checking"),
    access,
    accounts,
    sharedLoaded: s.sharedLoaded,
    sharedError: s.sharedError,
    profile,
    name: owner ? access?.name || profileName || owner : null,
    notice: s.notice,
    role: owner ? access?.role || null : "holder",
    scope: owner ? access?.scope || null : "account",
  };
}

export default function useWorkspace() {
  const s = useWorkspaceState();
  return useMemo(() => deriveWorkspace(s), [s]);
}

// Shared account whose entry hasn't loaded (or was just removed): nothing is
// allowed until it's known. The server decides anyway.
const unknownAccess = (owner) => ({
  owner,
  accountRole: null,
  eventRoles: {},
  events: [],
  slugs: [],
  role: null,
  scope: "event",
});

// Role checks for the current account. can(permission, event?) is true in
// your own account, and for your own events (tests) wherever you work.
export function teamAccess(ws) {
  const access = ws.isShared ? ws.access || unknownAccess(ws.owner) : null;
  return {
    ...ws,
    can: (permission, event) => {
      if (
        ws.isShared &&
        event &&
        typeof event === "object" &&
        event.broadcaster &&
        event.broadcaster === ws.self
      ) {
        return true;
      }
      return can(access, permission, event);
    },
  };
}

export function useTeamAccess() {
  const ws = useWorkspace();
  return useMemo(() => teamAccess(ws), [ws]);
}
